import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Home as HomeIcon, Ruler, ArrowRight, Lock } from 'lucide-react';
import { formatPrice, formatArea } from '../utils/format';

const PropertyCard = ({ property, onClick, isBlurred = false, onSignupPrompt, currencyPreference = 'USD', areaUnitPreference = 'sqm', fxRate = null }) => {
  const displayPrice = formatPrice(property, currencyPreference, fxRate);
  const areaDisplay = formatArea(property, areaUnitPreference);

  const imageUrl = (property.image_urls && property.image_urls.length > 0)
    ? property.image_urls[0]
    : (property.imageUrl && typeof property.imageUrl === 'string' && property.imageUrl.trim())
      ? property.imageUrl.trim()
      : 'https://via.placeholder.com/800x600?text=No+Image';

  const handleClick = () => {
    if (isBlurred && onSignupPrompt) {
      onSignupPrompt();
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={!isBlurred ? { y: -8, scale: 1.005 } : {}}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer flex flex-col h-full relative ${isBlurred ? 'opacity-75' : ''}`}
      onClick={handleClick}
    >
      {/* Blur Overlay */}
      {isBlurred && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-md z-20 flex flex-col items-center justify-center p-6 rounded-2xl">
          <div className="text-center">
            <Lock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 text-lg mb-2">Create an account to view</h3>
            <p className="text-sm text-gray-600 mb-4">
              Open an account to view your feature house!
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                if (onSignupPrompt) onSignupPrompt();
              }}
              className="px-6 py-2.5 bg-gray-900 text-white rounded-lg font-semibold text-sm hover:bg-gray-800 transition-colors"
            >
              Sign Up Free
            </motion.button>
          </div>
        </div>
      )}

      {/* Image Container */}
      <div className={`aspect-[4/3] relative bg-gradient-to-br from-gray-100 via-gray-50 to-gray-200 overflow-hidden ${isBlurred ? 'blur-sm' : ''}`}>
        <motion.img
          src={imageUrl}
          alt={property.title}
          className="w-full h-full object-cover"
          whileHover={!isBlurred ? { scale: 1.12 } : {}}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/800x600?text=No+Image';
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        
        {/* Energy Label Badge */}
        {property.energy_label && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute top-3 left-3 z-10"
          >
            <span className="px-2.5 py-1 rounded-lg backdrop-blur-sm text-[10px] font-bold uppercase tracking-wider shadow-md border border-white/40 bg-emerald-500 text-white">
              {property.energy_label}
            </span>
          </motion.div>
        )}

        {/* Price Badge */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 text-white z-10">
          <p className="text-[10px] font-medium opacity-90 uppercase tracking-wider mb-0.5">
            Monthly rent
          </p>
          <p className="text-2xl sm:text-3xl font-bold tracking-tight">{displayPrice}</p>
        </div>

        {/* Hover Overlay Effect */}
        {!isBlurred && (
          <motion.div
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent transition-opacity duration-300"
          />
        )}
      </div>

      {/* Content */}
      <div className={`p-4 sm:p-5 flex flex-col flex-1 ${isBlurred ? 'blur-sm' : ''}`}>
        {/* Title and Location */}
        <div className="mb-3">
          <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-tight line-clamp-2 group-hover:text-gray-700 transition-colors mb-1.5">
            {property.title || property.streetAddress || property.address || 'Listing'}
          </h3>
          <div className="flex items-center gap-1.5 text-gray-500 text-xs sm:text-sm">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
            <span className="truncate">{property.address || property.city}</span>
            {property.city && property.address && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-gray-400">{property.city}</span>
              </>
            )}
          </div>
        </div>

        {/* Specs Grid */}
        <div className="grid grid-cols-3 gap-2.5 mb-3">
          {(property.beds ?? property.rooms) !== undefined && (property.beds ?? property.rooms) !== null && (
            <motion.div
              whileHover={!isBlurred ? { scale: 1.03, y: -1 } : {}}
              className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 flex items-center gap-2.5 group/spec"
            >
              <div className="p-1.5 bg-white rounded-md shadow-sm text-gray-600 group-hover/spec:bg-gray-900 group-hover/spec:text-white transition-colors">
                <HomeIcon className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] text-gray-400 uppercase tracking-wide font-medium">Beds</p>
                <p className="text-sm font-bold text-gray-900">{property.beds ?? property.rooms}</p>
              </div>
            </motion.div>
          )}
          {property.baths != null && (
            <motion.div
              whileHover={!isBlurred ? { scale: 1.03, y: -1 } : {}}
              className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 flex items-center gap-2.5 group/spec"
            >
              <div className="p-1.5 bg-white rounded-md shadow-sm text-gray-600 group-hover/spec:bg-gray-900 group-hover/spec:text-white transition-colors">
                <HomeIcon className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] text-gray-400 uppercase tracking-wide font-medium">Baths</p>
                <p className="text-sm font-bold text-gray-900">{property.baths}</p>
              </div>
            </motion.div>
          )}
          {areaDisplay && (
            <motion.div
              whileHover={!isBlurred ? { scale: 1.03, y: -1 } : {}}
              className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 flex items-center gap-2.5 group/spec"
            >
              <div className="p-1.5 bg-white rounded-md shadow-sm text-gray-600 group-hover/spec:bg-gray-900 group-hover/spec:text-white transition-colors">
                <Ruler className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] text-gray-400 uppercase tracking-wide font-medium">Size</p>
                <p className="text-sm font-bold text-gray-900">{areaDisplay}</p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Furnished Badge */}
        {property.furnished && (
          <div className="mb-3">
            <span className="inline-block px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-[10px] font-semibold border border-gray-200">
              {property.furnished}
            </span>
          </div>
        )}

        {/* View Button */}
        <motion.div
          initial={{ opacity: 0.8 }}
          whileHover={!isBlurred ? { opacity: 1 } : {}}
          className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between group/button"
        >
          <span className="text-xs sm:text-sm font-medium text-gray-500 group-hover/button:text-gray-900 transition-colors">
            {isBlurred ? 'Sign up to view' : 'View details'}
          </span>
          <motion.div
            animate={{ x: 0 }}
            whileHover={!isBlurred ? { x: 3 } : {}}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            {isBlurred ? (
              <Lock className="w-4 h-4 text-gray-400" />
            ) : (
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover/button:text-gray-700 transition-colors" />
            )}
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PropertyCard;
